import React from "react";
import {Card, Col, Row, Spinner} from "react-bootstrap";
import {PiArrowDown, PiArrowUp, PiMinus} from "react-icons/pi";
import useStatisticsData from "./useStatisticsData.js";
import HelpTooltip from "../HelpTooltip.jsx";
import {
    formatDateDE,
    formatEuro,
    formatNumber,
    formatPercent,
    formatSignedPercent,
    TREND_DOWN_COLOR,
    TREND_NEUTRAL_COLOR,
    TREND_UP_COLOR,
} from "./format.js";

const METRICS = [
    {
        key: "net_amount",
        label: "Nettoumsatz",
        format: formatEuro,
        help: "Summe der Nettoumsätze im gewählten Zeitraum (Verkäufe minus Retouren).",
    },
    {
        key: "quantity",
        label: "Menge",
        format: (v) => `${formatNumber(v)} Stk.`,
        help: "Verkaufte Menge im gewählten Zeitraum, Retouren bereits gegengerechnet.",
    },
    {
        key: "line_count",
        label: "Positionen",
        format: formatNumber,
        help: "Anzahl der Belegpositionen (Auftrags-/Rechnungszeilen) im Zeitraum.",
    },
    {
        key: "article_count",
        label: "Artikelanzahl",
        format: formatNumber,
        help: "Anzahl unterschiedlicher Artikel mit mindestens einer Position im Zeitraum.",
    },
    {
        key: "customer_count",
        label: "Kunden",
        format: formatNumber,
        help: "Anzahl unterschiedlicher Kunden mit mindestens einer Position im Zeitraum.",
    },
    {
        key: "avg_line_value",
        label: "Ø Positionswert",
        format: formatEuro,
        help: "Nettoumsatz geteilt durch Anzahl Positionen.",
    },
    {
        key: "return_rate",
        label: "Retourenquote",
        format: (v) => formatPercent(v * 100),
        help: "Anteil der retournierten Menge an der insgesamt bewegten Menge (Verkauf + Retoure) im Zeitraum.",
    },
];

/**
 * Kennzahlen-Kacheln mit Veränderung zum gleich langen Vorzeitraum. Die
 * Veränderung wird immer durch Pfeil, Vorzeichen und Prozentwert
 * ausgedrückt - Farbe ist nur eine zusätzliche, nie die einzige Markierung.
 */
export default function KpiCards({filters}) {
    const {data, loading, error} = useStatisticsData("/statistics/kpis", filters);

    return (
        <div>
            <Row xs={1} sm={2} lg={4} className="g-3">
                {METRICS.map((metric) => {
                    const entry = data?.metrics?.[metric.key];
                    const changePct = entry?.change_pct;
                    const trend = changePct === null || changePct === undefined || Math.abs(changePct) < 0.05
                        ? "neutral"
                        : changePct > 0 ? "up" : "down";
                    const trendColor = trend === "up" ? TREND_UP_COLOR : trend === "down" ? TREND_DOWN_COLOR : TREND_NEUTRAL_COLOR;
                    const TrendIcon = trend === "up" ? PiArrowUp : trend === "down" ? PiArrowDown : PiMinus;
                    const signedPct = formatSignedPercent(changePct);

                    return (
                        <Col key={metric.key}>
                            <Card className="h-100 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span className="text-muted small text-uppercase">{metric.label}</span>
                                        <HelpTooltip placement="left" size={0.75}>{metric.help}</HelpTooltip>
                                    </div>
                                    {loading && !data ? (
                                        <div className="py-2">
                                            <Spinner animation="border" size="sm" role="status">
                                                <span className="visually-hidden">Wird geladen…</span>
                                            </Spinner>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="fs-4 fw-semibold lh-sm mt-1">{entry ? metric.format(entry.value) : "–"}</div>
                                            <div className="small mt-1 d-flex align-items-center gap-1" style={{color: trendColor}}>
                                                <TrendIcon aria-hidden="true" />
                                                <span className="text-body">
                                                    {signedPct ? `${signedPct} ggü. Vorzeitraum` : "keine Vergleichsbasis"}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
            {error && <div className="text-danger small mt-2">{error}</div>}
            {data?.period && (
                <div className="text-muted small mt-2">
                    Zeitraum {formatDateDE(data.period.from)}–{formatDateDE(data.period.to)}, verglichen mit
                    Vorzeitraum {formatDateDE(data.previous_period.from)}–{formatDateDE(data.previous_period.to)}.
                </div>
            )}
        </div>
    );
}
